// map/utils/hexLatticeConverter.ts
//
// Converts an arbitrary list of { lat, lon, weight } points — e.g. a plain
// rectangular lat/lon grid from an external producer — into cells snapped
// onto a proper hex lattice, ready to hand to
// PdarProbabilityFieldManager's hexagonRing() for rendering.
//
// WHY THIS EXISTS: another team's probability-field producer generates its
// own field on a plain rectangular grid (stepping lat/lon independently,
// no half-hex-width offset between rows). That's a reasonable way to
// generate raw sample points, but it does NOT tile as hexagons on its own
// — PdarProbabilityFieldManager draws exactly the hexagon it's told to, at
// exactly the center it's given, with no snapping or grid inference of its
// own. Feeding it non-lattice centers produces correctly-shaped individual
// hexagons that don't line up with their neighbors (oversized-looking,
// overlapping, with diamond gaps at row junctions).
//
// multi.sim.ts can simulate this exact bug (see its
// SIMULATE_RAW_EXTERNAL_GRID flag / buildRawRectangularGrid) so the fix
// here can be tested end-to-end against realistic bad input before the
// other team needs to change anything on their side — this app converts
// whatever shape of grid it receives on probability-grid:batch into a
// valid tiling before ever building polygon geometry from it.
import type { Position } from "../types";

export type RawGridPoint = {
  lat: number;
  lon: number;
  weight: number;
};

export type HexLatticeCell = {
  lat: number;
  lon: number;
  weight: number;
};

export type ConvertToHexLatticeOptions = {
  // Every output cell's {lat, lon} is expressed relative to this origin —
  // pick any fixed point (e.g. the target's own reported position). The
  // lattice itself is centered here (q=0, r=0), so the SAME origin must be
  // used for every batch converted for a given target if you want cells
  // across successive ticks to stay on a consistent tiling.
  origin: Position;
  // Center-to-vertex hex radius, metres. Should match hex_radius_metres as
  // published on probability-grid:batch, so rendered hexagons are sized
  // consistently with the lattice spacing used here.
  hexRadiusMetres: number;
  // When multiple raw points snap to the same hex cell (common — a source
  // grid finer than the hex spacing will do this constantly), their
  // weights are combined via this strategy. Defaults to "max": the point
  // that most strongly supports a cell's probability should win, rather
  // than being diluted by weaker nearby samples.
  mergeStrategy?: "max" | "average" | "sum";
};

const EARTH_RADIUS_M = 6371000;
const toRad = (d: number) => (d * Math.PI) / 180;
const toDeg = (r: number) => (r * 180) / Math.PI;

// Local equirectangular approximation (east/north metres from an origin) —
// accurate at the scale hex fields operate at (tens of metres to a few
// km), matching the approach used throughout the simulator.
function toLocalMetres(
  origin: Position,
  point: Position,
): { eastM: number; northM: number } {
  const dLat = toRad(point.lat - origin.lat);
  const dLon = toRad(point.lon - origin.lon);
  const northM = dLat * EARTH_RADIUS_M;
  const eastM = dLon * EARTH_RADIUS_M * Math.cos(toRad(origin.lat));
  return { eastM, northM };
}

function fromLocalMetres(
  origin: Position,
  eastM: number,
  northM: number,
): Position {
  const latOffset = northM / EARTH_RADIUS_M;
  const lonOffset = eastM / (EARTH_RADIUS_M * Math.cos(toRad(origin.lat)));
  return {
    lat: origin.lat + toDeg(latOffset),
    lon: origin.lon + toDeg(lonOffset),
  };
}

// Snaps an arbitrary (eastM, northM) offset onto the nearest cell of a
// pointy-top axial hex lattice of the given radius, centered on (0, 0).
// Uses cube-coordinate rounding (the standard technique for snapping a
// fractional axial coordinate to its true nearest hex — rounding q and r
// independently can pick the WRONG neighboring hex near a cell boundary).
function snapToHexLattice(
  eastM: number,
  northM: number,
  hexRadiusMetres: number,
): { q: number; r: number; eastM: number; northM: number } {
  const hexWidthM = hexRadiusMetres * Math.sqrt(3);
  const hexHeightM = hexRadiusMetres * 1.5;

  // Fractional axial coords (inverse of q/r -> eastM/northM: eastM =
  // hexWidthM*(q + r/2), northM = hexHeightM*r — the "+ r/2" term is the
  // row-offset a plain rectangular grid never applies, which is exactly
  // why raw rectangular points need this snap step).
  const rFrac = northM / hexHeightM;
  const qFrac = eastM / hexWidthM - rFrac / 2;

  const xFrac = qFrac;
  const zFrac = rFrac;
  const yFrac = -xFrac - zFrac;

  let rx = Math.round(xFrac);
  let ry = Math.round(yFrac);
  let rz = Math.round(zFrac);

  const xDiff = Math.abs(rx - xFrac);
  const yDiff = Math.abs(ry - yFrac);
  const zDiff = Math.abs(rz - zFrac);

  if (xDiff > yDiff && xDiff > zDiff) {
    rx = -ry - rz;
  } else if (yDiff > zDiff) {
    ry = -rx - rz;
  } else {
    rz = -rx - ry;
  }

  const q = rx;
  const r = rz;
  return {
    q,
    r,
    eastM: hexWidthM * (q + r / 2),
    northM: hexHeightM * r,
  };
}

// Converts an arbitrary list of {lat, lon, weight} points — on any grid
// shape, rectangular or otherwise — into a deduplicated list of cells that
// sit exactly on a valid hex lattice.
//
// Multiple raw points commonly snap to the same hex (especially if the
// source grid is finer than the hex spacing); mergeStrategy controls how
// their weights combine into that one output cell.
export function convertToHexLattice(
  rawPoints: RawGridPoint[],
  options: ConvertToHexLatticeOptions,
): HexLatticeCell[] {
  const { origin, hexRadiusMetres, mergeStrategy = "max" } = options;

  const byHex = new Map<
    string,
    { eastM: number; northM: number; weights: number[] }
  >();

  for (const raw of rawPoints) {
    const { eastM, northM } = toLocalMetres(origin, {
      lat: raw.lat,
      lon: raw.lon,
    });
    const hex = snapToHexLattice(eastM, northM, hexRadiusMetres);
    const key = `${hex.q}:${hex.r}`;

    const existing = byHex.get(key);
    if (existing) {
      existing.weights.push(raw.weight);
    } else {
      byHex.set(key, {
        eastM: hex.eastM,
        northM: hex.northM,
        weights: [raw.weight],
      });
    }
  }

  const cells: HexLatticeCell[] = [];
  for (const { eastM, northM, weights } of byHex.values()) {
    const point = fromLocalMetres(origin, eastM, northM);
    let weight: number;
    switch (mergeStrategy) {
      case "average":
        weight = weights.reduce((sum, w) => sum + w, 0) / weights.length;
        break;
      case "sum":
        weight = weights.reduce((sum, w) => sum + w, 0);
        break;
      case "max":
      default:
        weight = Math.max(...weights);
        break;
    }
    cells.push({ lat: point.lat, lon: point.lon, weight });
  }

  return cells;
}
